<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <router-link class="subtitle-1 grey-navy--text font-weight-bold text-decoration-none" :to="backLink">
          <v-icon small class="pb-1"> keyboard_backspace </v-icon> Back
        </router-link>
      </v-col>
    </v-row>
    <v-row v-if="loading">
      <v-col cols="12" align="center" justify="center">
        <v-row align="center" justify="center">
          <v-text-field color="success" loading disabled></v-text-field>
        </v-row>
      </v-col>
    </v-row>

    <v-row v-if="!loading && subscription">
      <v-col cols="4">
        <h2 class="pl-3">Onboarding for:</h2>
        <client-information :userId="userId"></client-information>
      </v-col>
      <v-col cols="8">
        <v-row>
          <v-col>
            <h2>Coach: {{ subscription.node.coach.firstName }} {{ subscription.node.coach.lastName }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h2>Initial Consultation Prep</h2>
            <v-stepper non-linear vertical class="my-2">
              <v-stepper-step
                :complete="checkPresent('PERSONAL_INFORMATION_REVIEWED')"
                edit-icon="$complete"
                editable
                class="text-uppercase"
                step="1"
              >
                Review Personal Information
              </v-stepper-step>

              <v-stepper-content step="1">
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-btn
                      color="primary"
                      @click="checkOffStep('PERSONAL_INFORMATION_REVIEWED')"
                      :disabled="checkPresent('PERSONAL_INFORMATION_REVIEWED')"
                    >
                      {{ checkPresent("PERSONAL_INFORMATION_REVIEWED") ? "Completed" : "Complete Step" }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>

              <v-stepper-step
                :complete="checkPresent('INTRO_VIDEO_SENT')"
                editable
                edit-icon="$complete"
                class="text-uppercase"
                step="2"
              >
                Introduction Video Sent To Client
              </v-stepper-step>

              <v-stepper-content step="2">
                <v-row>
                  <v-col cols="12" class="py-0">
                    <v-btn
                      color="primary"
                      @click="checkOffStep('INTRO_VIDEO_SENT')"
                      :disabled="checkPresent('INTRO_VIDEO_SENT')"
                    >
                      {{ checkPresent("INTRO_VIDEO_SENT") ? "Completed" : "Complete Step" }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>

              <v-stepper-step
                :complete="checkPresent('MEDICAL_ASSESSMENT_REVIEWED')"
                edit-icon="$complete"
                editable
                class="text-uppercase"
                step="3"
              >
                Review Latest Medical Assessment
              </v-stepper-step>

              <v-stepper-content step="3">
                <v-row>
                  <v-col cols="12" class="py-0">
                    <client-medical-assessment :userId="userId" />
                    <br />
                    <v-btn
                      color="primary"
                      @click="checkOffStep('MEDICAL_ASSESSMENT_REVIEWED')"
                      :disabled="checkPresent('MEDICAL_ASSESSMENT_REVIEWED')"
                    >
                      {{ checkPresent("MEDICAL_ASSESSMENT_REVIEWED") ? "Completed" : "Complete Step" }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>

              <v-stepper-step
                :complete="checkPresent('ASSESSMENT_REVIEWED')"
                edit-icon="$complete"
                editable
                class="text-uppercase"
                step="4"
              >
                Review initial check-in
              </v-stepper-step>

              <v-stepper-content step="4">
                <v-row>
                  <v-col cols="12" class="py-0">
                    <assessment
                      v-if="initialAssessment"
                      :userId="userId"
                      :assessmentId="initialAssessment.id"
                    ></assessment>
                    <span v-else>
                      No assessment booked
                    </span>
                    <v-btn
                      color="primary"
                      @click="checkOffStep('ASSESSMENT_REVIEWED')"
                      :disabled="checkPresent('ASSESSMENT_REVIEWED')"
                    >
                      {{ checkPresent("ASSESSMENT_REVIEWED") ? "Completed" : "Complete Step" }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>
            </v-stepper>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h2>During Initial Consultation</h2>
            <v-stepper non-linear vertical class="my-2">
              <v-stepper-step
                :complete="checkPresent('PROGRAM_START_DATE_CONFIRMED')"
                edit-icon="$complete"
                editable
                class="text-uppercase"
                step="1"
              >
                Set program start date
              </v-stepper-step>

              <v-stepper-content step="1">
                <v-row>
                  <v-col cols="5">
                    <v-menu
                      v-model="menu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="subscription.node.programStartDate"
                          label="Program start date"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          :success="subscription.node.programStartDate != null"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="subscription.node.programStartDate"
                        @input="menu = false"
                        @change="updateProgramStartDate($event)"
                        :allowed-dates="allowedDates"
                      ></v-date-picker>
                    </v-menu>
                    <v-btn
                      color="primary"
                      @click="checkOffStep('PROGRAM_START_DATE_CONFIRMED')"
                      :disabled="checkPresent('PROGRAM_START_DATE_CONFIRMED')"
                    >
                      {{ checkPresent("PROGRAM_START_DATE_CONFIRMED") ? "Completed" : "Complete Step" }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>

              <v-stepper-step
                :complete="checkPresent('GOAL_CREATED')"
                edit-icon="$complete"
                editable
                class="text-uppercase"
                step="2"
              >
                Set goals for client
              </v-stepper-step>

              <v-stepper-content step="2">
                <v-row>
                  <v-col cols="12" class="py-0" justify="left" align="left">
                    <goals :userId="userId"></goals>
                    <v-btn
                      color="primary"
                      @click="checkOffStep('GOAL_CREATED')"
                      :disabled="checkPresent('GOAL_CREATED')"
                    >
                      {{ checkPresent("GOAL_CREATED") ? "Completed" : "Complete Step" }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-stepper-content>

              <v-stepper-step
                :complete="nutritionalPlanId && nextProgramId"
                editable
                step="3"
                class="text-uppercase"
                edit-icon="$complete"
              >
                Setup program
              </v-stepper-step>

              <v-stepper-content step="3">
                <!-- old nutrition plan, no longer in use -->
                <!-- <v-row>
                  <v-col cols="12" class="px-6 py-0">
                    <h2>Set Nutrition Plan <v-icon @click="showModal()">functions</v-icon></h2>
                    <v-autocomplete
                      v-model="nutritionalPlanId"
                      :items="nutritionOptions"
                      clearable
                      solo
                      label="Nutrition Plan"
                      @change="changeNutritionPlan($event)"
                    ></v-autocomplete>
                  </v-col>
                </v-row> -->
                <v-row>
                  <v-col cols="12">
                    <block-history :userId="subscription.node.user.id" :allowCreate="true"></block-history>
                    <nutrition-block-history :userId="subscription.node.user.id"></nutrition-block-history>
                  </v-col>
                </v-row>
              </v-stepper-content>
            </v-stepper>
            <v-dialog v-model="nutritionModal" width="900">
              <v-card>
                <v-card-title class="headline grey lighten-2">
                  Nutrition Calculator
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="4">
                      <h4>
                        Gender:
                        <b>{{
                          subscription.node.user.userProfile.gender
                            ? subscription.node.user.userProfile.gender
                            : "No gender set"
                        }}</b>
                      </h4>
                    </v-col>
                    <v-col cols="4">
                      <h4>
                        Age: <b>{{ age }}</b>
                      </h4>
                    </v-col>
                    <v-col cols="4">
                      <h4>
                        Height:
                        <b>{{
                          subscription.node.user.userProfile.height
                            ? subscription.node.user.userProfile.height
                            : "No gender set"
                        }}</b>
                      </h4>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field v-model="manualWeight" label="Weight"></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field v-model="calorie" label="Manual Exercise Calorie Input"></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn class="ml-4" :disabled="canCalculate()" @click="calculate()">Calculate</v-btn>
                </v-card-actions>
                <v-card-text>
                  <v-row>
                    <v-col cols="3">
                      <h4>Resting Metaboic Rate** (kcal)</h4>
                      {{ calcs.thermaticRest }}
                    </v-col>
                    <v-col cols="3">
                      <h4>Resting Metaboic Rate** (kcal)</h4>
                      {{ calcs.thermaticDigest }}
                    </v-col>
                    <v-col cols="3">
                      <h4>Non Exercise Activity (kcal)</h4>
                      {{ calcs.nonExercise }}
                    </v-col>
                    <v-col cols="3">
                      <h4>Non Exercise Activity (kcal)</h4>
                      {{ calcs.exercise }}
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4">
                      <h4>Total Daily Enery Expenditure (kcal)</h4>
                      {{ calcs.total }}
                    </v-col>
                    <v-col cols="4">
                      <h4>Total Daily Enery Expenditure (kcal) manual entry</h4>
                      {{ calcs.totalManual }}
                    </v-col>
                    <v-col cols="4">
                      <h4>Suggested Plan</h4>
                      <b>{{ plan }}</b>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  CLIENT_WITH_ONBOARDING_QUERY,
  UPSERT_SUBSCRIPTION_CHECK_MUTATION,
} from "@/constants/graphql/operations/onboarding";
import { UPDATE_SUBSCRIPTION_MUTATION } from "@/constants/graphql/users/subscription";
import { NUTRITION_PLANS_QUERY, UPDATE_NUTRITION_PLAN_MUTATION } from "@/constants/graphql/nutrition/nutrition";
import { UPDATE_USER_PROGRAM_MUTATION } from "@/constants/graphql/user_program/user_program";
import { PROGRAMS_QUERY } from "@/constants/graphql/user_program/user_program";
import ClientInformation from "@/components/client/blocks/ClientInformation";
import ClientMedicalAssessment from "@/components/client/blocks/ClientMedicalAssessment";
import Goals from "@/components/client/blocks/progress/assessments/goals/Goals";
import Assessment from "@/components/client/blocks/progress/assessments/Assessment";
import BlockHistory from "@/components/workout_blocks/BlockHistory";
import NutritionBlockHistory from "@/components/workout_blocks/NutritionBlockHistory";

export default {
  name: "CoachOnboarding",
  props: ["userId"],
  components: {
    ClientInformation,
    ClientMedicalAssessment,
    Goals,
    Assessment,
    BlockHistory,
    NutritionBlockHistory,
  },
  data() {
    return {
      loading: true,
      subscription: null,
      initialAssessment: null,
      menu: false,
      manualWeight: null,
      nutritionModal: false,
      calorie: 0,
      nutritionPlans: [],
      nutritionOptions: [],
      nutritionalPlanId: null,
      calcs: {
        thermaticRest: null,
        thermaticDigest: null,
        nonExercise: null,
        exercise: null,
        total: null,
        totalManual: null,
      },
      plan: null,
      allowedDates: (val) => new Date(val).getUTCDay() === 1,
    };
  },
  computed: {
    backLink() {
      if (this.subscription && this.subscription.node.coach) {
        return `/client-profile/${this.subscription.node.coach.id}/${this.userId}`;
      } else {
        return "/dashboard";
      }
    },
    age() {
      if (this.subscription.node.user?.userProfile?.dateOfBirth) {
        let dob = this.$moment(this.subscription.node.user.userProfile.dateOfBirth).format("YYYY-MM-DD");
        let age = this.$moment().diff(dob, "years");

        return age;
      }

      return "No date of birth";
    },
  },
  methods: {
    canCalculate() {
      return (
        this.manualWeight == null ||
        this.subscription.node.user.userProfile.height == null ||
        this.subscription.node.user.userProfile.gender == null ||
        this.age == "No date of birth"
      );
    },
    showModal() {
      this.nutritionModal = true;
    },
    checkPresent(type) {
      if (this.subscription == null) {
        return false;
      }
      let checks = this.subscription.node.subscriptionProcessChecks;
      if (checks.length == 0) {
        return false;
      }
      return checks.filter((check) => check.type == type).length > 0;
    },
    getId(base64string) {
      let decoded = atob(base64string).split(":");
      if (decoded[1]) return decoded[1];
      return "";
    },
    calculate() {
      // calculate suggested nutrition plan based
      if (this.subscription.node.user.userProfile.gender == "FEMALE") {
        this.calcs.thermaticRest = parseInt(
          9.99 * parseFloat(this.manualWeight) +
            6.25 * parseInt(this.subscription.node.user.userProfile.height) -
            4.92 * parseInt(this.age) -
            161
        );
      } else {
        this.calcs.thermaticRest = parseInt(
          9.99 * parseFloat(this.manualWeight) +
            6.25 * parseInt(this.subscription.node.user.userProfile.height) -
            4.92 * parseInt(this.age) +
            5
        );
      }
      this.calcs.thermaticDigest = parseInt(this.calcs.thermaticRest * 0.08);
      this.calcs.nonExercise = parseInt(this.calcs.thermaticRest * 0.15);
      this.calcs.exercise = parseInt(parseFloat(this.manualWeight) * 5.5);
      this.calcs.total =
        this.calcs.thermaticRest + this.calcs.exercise + this.calcs.thermaticDigest + this.calcs.nonExercise;
      this.calcs.totalManual = this.calorie
        ? this.calcs.thermaticRest + this.calcs.calorie + this.calcs.thermaticDigest + this.calcs.nonExercise
        : 0;
      this.plan = Math.ceil((this.calcs.thermaticRest - this.calcs.thermaticRest * 0.15) / 300.0) * 300;
    },
    changeNutritionPlan(evt) {
      this.saving = true;

      this.$apollo
        .mutate({
          mutation: UPDATE_NUTRITION_PLAN_MUTATION,
          variables: {
            userId: this.userId,
            nutritionPlanId: evt,
          },
        })
        .then((result) => {
          if (!result.data.updateNutritionPlan.updated) {
            alert(result.data.updateNutritionPlan.error);
          } else {
            this.$toasted.success("Nutrition Plan updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
          this.saving = false;
        })
        .catch((error) => {
          this.saving = false;
        });
    },
    updateProgramStartDate(event) {
      this.mutateSubscription(event);
    },
    mutateSubscription(date) {
      this.$apollo
        .mutate({
          mutation: UPDATE_SUBSCRIPTION_MUTATION,
          variables: {
            input: {
              id: this.getId(this.subscription.node.id),
              programStartDate: date,
            },
          },
        })
        .then((result) => {
          if (!result.data.updateSubscriptionRecord.updated) {
            alert(result.data.updateSubscriptionRecord.error);
          } else {
            this.$toasted.success("Updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
    checkOffStep(type) {
      this.$apollo
        .mutate({
          mutation: UPSERT_SUBSCRIPTION_CHECK_MUTATION,
          variables: {
            input: {
              subscriptionId: this.getId(this.subscription.node.id),
              type: type.toLowerCase(),
            },
          },
        })
        .then((result) => {
          if (!result.data.upsertSubscriptionCheckRecord.success) {
            alert(result.data.upsertSubscriptionCheckRecord.error);
          } else {
            this.subscription.node.subscriptionProcessChecks.push({ type: type });
            this.$toasted.success("Success", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
  },
  apollo: {
    userSubscriptions: {
      query: CLIENT_WITH_ONBOARDING_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        return {
          userId: encoded_json_payload,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.subscription = response.data.userSubscriptions.edges[0];
          if (this.subscription) {
            var assessments = this.subscription.node.coachingAssessments.filter((assessment) => {
              return this.$moment(assessment.requiredDate) >= this.$moment(this.subscription.node.startDate);
            });
            this.initialAssessment = assessments.length > 0 ? assessments[0] : null;
            this.onboardingProgram = this.subscription.node.onboardingProgram;
            if (this.onboardingProgram) {
              this.nextProgramId = this.onboardingProgram.nextProgram.id ? this.onboardingProgram.nextProgram.id : null;
              this.nutritionalPlanId = this.onboardingProgram.nutritionPlan.id
                ? this.onboardingProgram.nutritionPlan.id
                : null;
            }
          }

          this.loading = false;
        }
      },
    },
    nutritionPlans: {
      query: NUTRITION_PLANS_QUERY,
      result(response) {
        this.nutritionOptions = response.data.nutritionPlans.map((val) => {
          let name = val.name.replace("Nutrition Plan", "");
          return { value: val.id, text: name };
        });
        this.nutritionOptions.sort((a, b) => (a.text > b.text ? 1 : -1));
        this.loading = false;
      },
    },
  },
};
</script>
